import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { includes, map, clone, find, get } from 'lodash';
import {
  MenuItem,
  InputLabel,
  FormControl,
  Button,
  Input,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import styles from './VendorAttribution.module.css';
import {
  ConsumerSegments,
  Categories,
  Classifications,
  Regions,
} from '../../Lib/Constants/vendor-attribution';
import CountryList from '../../Lib/Constants/countries';
import SelectInput from '../ReusableComponents/SelectInput/SelectInput';
import TextInput from '../ReusableComponents/TextInput/TextInput';

const useStyles = makeStyles()((theme) => ({
  selected: {
    color: 'white',
    background: get(theme, 'palette.primary.main'),
    '&:hover': {
      background: get(theme, 'palette.primary.light'),
    },
  },
  notSelected: {
    color: get(theme, 'palette.disableGray.main'),
    background: get(theme, 'palette.bg.main'),
    '&:hover': {
      background: theme.palette.primary.dark,
    },
  },
  start: {
    width: '105px',
    borderRadius: '4px 0px 0px 4px',
  },
  end: {
    width: '105px',
    borderRadius: '0px 4px 4px 0px',
  },
  middle: {
    width: '105px',
    borderRadius: '0px',
  },
}));

function containsOther(classifications) {
  const otherValue = find(
    classifications,
    (classification) => !includes(Classifications, classification),
  );

  return otherValue;
}

export default function AttributionSelectInputs(props) {
  const { classes, cx } = useStyles();
  const {
    values: {
      consumer_segment,
      fabric_classification,
      product_type,
      region,
      country,
    },
    emptyFields,
    onChange,
    onBlur,
    onDelete,
    disabled,
  } = props;

  const [fields, setValues] = useState({
    consumer_segment,
    fabric_classification,
    product_type,
    region,
    country,
  });

  const [otherSelected, toggleOther] = useState(
    !!containsOther(fabric_classification),
  );

  const onOtherChange = ({ target: { value } }) => {
    const filtered = fabric_classification.filter((classification) =>
      includes(Classifications, classification),
    );

    onChange('fabric_classification', filtered.concat([value]));
  };

  const onSelectChange = ({ target: { name, value } }) => {
    onChange(name, value);
    setValues({ ...fields, [name]: value });
  };

  const onSelectBlur = ({ target: { name, value } }) => {
    onBlur(name, value);
  };

  function toggleButtonSelect(name) {
    const copiedClassifications = map(fabric_classification, clone);

    if (includes(fabric_classification, name)) {
      const filtered = copiedClassifications.filter(
        (classification) => classification !== name,
      );
      onChange('fabric_classification', filtered);
    } else {
      onChange('fabric_classification', copiedClassifications.concat([name]));
    }
  }

  const handleOther = () => {
    if (otherSelected) {
      const filtered = fabric_classification.filter((classification) =>
        includes(Classifications, classification),
      );

      onChange('fabric_classification', filtered);
    }

    return toggleOther(!otherSelected);
  };

  function isSelected(value) {
    if (includes(fabric_classification, value)) return true;
    return false;
  }

  return (
    <div>
      <div className={styles.selectContainer}>
        <SelectInput
          multiSelect
          required
          className={styles.multiSelect}
          error={!!emptyFields.consumer_segment}
          label="Consumer Segment"
          multiple
          name="consumer_segment"
          onChange={onSelectChange}
          onBlur={onSelectBlur}
          value={consumer_segment}
          id="vatt-consumer-segment"
          input={<Input name="consumer_segment" />}
          options={ConsumerSegments.map((name) => (
            <MenuItem key={name} value={name}>
              {name}
            </MenuItem>
          ))}
          onDelete={onDelete}
          disabled={disabled}
        />
        <div className={styles.buttonBlockContainer}>
          <FormControl
            variant="standard"
            required
            className={styles.buttonBlock}
            error={!!emptyFields.fabric_classification}
          >
            <div className={styles.buttonBlockLabel}>
              <InputLabel
                id="vatt-fabric-classification-label"
                className={cx(styles.blockLabel, {
                  [styles.blockError]: !!emptyFields.fabric_classification,
                })}
              >
                Fabric Classification
              </InputLabel>
            </div>
            <Button
              className={cx(classes.start, {
                [classes.selected]: isSelected('knit'),
                [classes.notSelected]: !isSelected('knit'),
              })}
              variant="contained"
              name="knit"
              value="knit"
              onClick={() => toggleButtonSelect('knit')}
              disabled={disabled}
            >
              Knit
            </Button>
            <Button
              className={cx(classes.middle, {
                [classes.selected]: isSelected('sweater'),
                [classes.notSelected]: !isSelected('sweater'),
              })}
              variant="contained"
              name="sweater"
              value="sweater"
              onClick={() => toggleButtonSelect('sweater')}
              disabled={disabled}
            >
              Sweater
            </Button>
            <Button
              className={cx(classes.middle, {
                [classes.selected]: isSelected('woven'),
                [classes.notSelected]: !isSelected('woven'),
              })}
              variant="contained"
              name="woven"
              value="woven"
              onClick={() => toggleButtonSelect('woven')}
              disabled={disabled}
            >
              Woven
            </Button>
            <Button
              className={cx(classes.middle, {
                [classes.selected]: isSelected('denim'),
                [classes.notSelected]: !isSelected('denim'),
              })}
              variant="contained"
              name="denim"
              value="denim"
              onClick={() => toggleButtonSelect('denim')}
              disabled={disabled}
            >
              Denim
            </Button>
            <Button
              className={cx(classes.end, {
                [classes.selected]: otherSelected,
                [classes.notSelected]: !otherSelected,
              })}
              variant="contained"
              name="other"
              value="other"
              onClick={handleOther}
              disabled={disabled}
            >
              Other
            </Button>
          </FormControl>
        </div>
        <div>
          {otherSelected && (
            <TextInput
              required
              className={styles.otherInput}
              error={!!emptyFields.fabric_classification}
              label="Other"
              variant="outlined"
              onChange={onOtherChange}
              onBlur={onSelectBlur}
              name="other"
              value={containsOther(fields.fabric_classification)}
              id="vatt-other"
              autoComplete="no"
              disabled={disabled}
            />
          )}
        </div>
        <div>
          <SelectInput
            multiSelect
            required
            className={styles.multiSelect}
            error={!!emptyFields.product_type}
            label="Product Type/Category"
            multiple
            name="product_type"
            onChange={onSelectChange}
            onBlur={onSelectBlur}
            value={product_type}
            id="vatt-product_type"
            input={<Input name="product_type" />}
            options={Categories.map((name) => (
              <MenuItem key={name} value={name}>
                {name}
              </MenuItem>
            ))}
            onDelete={onDelete}
            disabled={disabled}
          />
        </div>
        <div>
          <SelectInput
            multiSelect
            required
            className={styles.multiSelect}
            error={!!emptyFields.region}
            label="Region"
            multiple
            name="region"
            onChange={onSelectChange}
            onBlur={onSelectBlur}
            value={region}
            id="vatt-region"
            input={<Input name="region" />}
            options={Regions.map((name) => (
              <MenuItem key={name} value={name}>
                {name}
              </MenuItem>
            ))}
            onDelete={onDelete}
            disabled={disabled}
          />
        </div>
        <div>
          <SelectInput
            multiSelect
            required
            className={styles.multiSelect}
            error={!!emptyFields.country}
            label="Country of Origin"
            multiple
            name="country"
            onChange={onSelectChange}
            onBlur={onSelectBlur}
            value={country}
            id="vatt-country"
            input={<Input name="country" />}
            options={CountryList.map((c) => (
              <MenuItem key={c.value} value={c.value}>
                {c.text}
              </MenuItem>
            ))}
            onDelete={onDelete}
            disabled={disabled}
          />
        </div>
      </div>
    </div>
  );
}

AttributionSelectInputs.propTypes = {
  values: PropTypes.shape({
    consumer_segment: PropTypes.array.isRequired,
    fabric_classification: PropTypes.array.isRequired,
    product_type: PropTypes.array.isRequired,
    region: PropTypes.array.isRequired,
    country: PropTypes.array.isRequired,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func.isRequired,
  emptyFields: PropTypes.shape({}).isRequired,
  onDelete: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};
